import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ApiService, UserGroup } from '@core/api/angular';
import { confirmSignup } from '@core/api/angular/operations/confirm-signup';
import { getJobCategories } from '@core/api/angular/operations/get-job-categories';
import { resendCode } from '@core/api/angular/operations/resend-code';
import { signup } from '@core/api/angular/operations/signup';
import { processMobileNumber } from '@lassi/util';
import {
  ConfirmSignUpOutput,
  ResendSignUpCodeOutput,
  SignUpOutput,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import {
  catchError,
  firstValueFrom,
  from,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { AMPLIFY_AUTH } from './auth';
import { LoginForm, SignupForms } from './auth.model';
import { RouteUtilsService } from './route-utils.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiService = inject(ApiService);
  private readonly auth = inject(AMPLIFY_AUTH);
  private readonly routeUtils = inject(RouteUtilsService);

  private readonly userEvents$ = new Observable<string>((sub) => {
    Hub.listen('auth', ({ payload }) => {
      sub.next(payload.event);

      return () => {
        sub.unsubscribe();
      };
    });
  });

  readonly currentUser$ = merge(
    from(this.auth.getCurrentUser()).pipe(
      catchError(() => of(null)),
      shareReplay(1),
    ),
    this.userEvents$.pipe(
      mergeMap((request) => {
        switch (request) {
          case 'signedIn':
          case 'signInWithRedirect':
          case 'tokenRefresh':
            return from(this.auth.getCurrentUser());

          case 'signedOut':
            return of(null);

          default:
            console.info('unknown auth event', request);
            return of(null);
        }
      }),
      shareReplay(1),
    ),
  );

  readonly userGroups$ = this.currentUser$.pipe(
    switchMap((user) =>
      !user
        ? of(null)
        : from(this.auth.fetchAuthSession()).pipe(
            map(
              (session) =>
                (session.tokens?.accessToken.payload['cognito:groups'] ??
                  []) as UserGroup[],
            ),
            map((userRoles) =>
              userRoles.reduce(
                (acc, cur) => ({
                  ...acc,
                  [cur]: true,
                }),
                {} as Record<UserGroup, true>,
              ),
            ),
          ),
    ),
    shareReplay(1),
  );

  readonly isAdmin = toSignal(
    this.userGroups$.pipe(
      map(
        (userGroups) =>
          !!(
            userGroups?.[UserGroup.LASSI_ADMIN] ||
            userGroups?.[UserGroup.SMES_ADMIN] ||
            userGroups?.[UserGroup.VICNAMES_ADMIN] ||
            userGroups?.[UserGroup.DQA_ADMIN]
          ),
      ),
    ),
  );

  readonly isSmesAdmin = toSignal(
    this.userGroups$.pipe(
      map(
        (userGroups) =>
          !!(
            userGroups?.[UserGroup.LASSI_ADMIN] ||
            userGroups?.[UserGroup.SMES_ADMIN]
          ),
      ),
    ),
  );

  readonly isSmesUser = toSignal(
    this.userGroups$.pipe(
      map(
        (userGroups) =>
          !!(
            userGroups?.[UserGroup.LASSI_ADMIN] ||
            userGroups?.[UserGroup.SMES_ADMIN] ||
            userGroups?.[UserGroup.SMES_USER]
          ),
      ),
    ),
  );

  readonly isVicnamesAdmin = toSignal(
    this.userGroups$.pipe(
      map(
        (userGroups) =>
          !!(
            userGroups?.[UserGroup.LASSI_ADMIN] ||
            userGroups?.[UserGroup.VICNAMES_ADMIN]
          ),
      ),
    ),
  );

  readonly userAttributes = toSignal(
    this.currentUser$.pipe(
      switchMap((user) =>
        !user ? of(null) : from(this.auth.fetchUserAttributes()),
      ),
    ),
  );

  readonly userId = computed(() => this.userAttributes()?.sub);

  readonly userName$ = this.currentUser$.pipe(
    switchMap((user) =>
      !user
        ? of(null)
        : from(this.auth.fetchUserAttributes()).pipe(
            map((user) => {
              return user.given_name + ' ' + user.family_name;
            }),
          ),
    ),
    shareReplay(1),
  );

  public canActivate(role: UserGroup | Array<UserGroup>) {
    const roles = Array.isArray(role) ? role : [role];

    return this.userGroups$.pipe(
      map((groups) => roles.some((role) => groups?.[role])),
    );
  }

  readonly isLoggedIn$ = this.currentUser$.pipe(
    map((user) => !!user),
    shareReplay(1),
  );

  readonly isLoggedIn = toSignal(this.isLoggedIn$);

  async signout() {
    await this.auth.signOut();
    return this.routeUtils.goToLogout();
  }

  signin(form: LoginForm) {
    return this.auth.signIn({
      username: form.email,
      password: form.password,
    });
  }

  confirmSignin(challengeResponse: string, phoneNumber?: string) {
    return this.auth.confirmSignIn({
      challengeResponse,
      ...(phoneNumber
        ? {
            options: {
              userAttributes: {
                phone_number: processMobileNumber(phoneNumber),
              },
            },
          }
        : {}),
    });
  }

  signup(form: SignupForms): Promise<SignUpOutput> {
    return firstValueFrom(
      this.apiService
        .invoke(signup, {
          body: {
            email: form.email,
            password: form.password,
            first_name: form.firstName,
            last_name: form.lastName,
            phone_number: form.phoneNumber!,
            job_category: form.jobCategory || undefined,
            organisation_name: form.organisationName || undefined,
            request_smes_signup: form.requestSmesUser,
          },
        })
        .pipe(
          map((res) => {
            const isComplete = res.next_step === 'DONE';

            const output: SignUpOutput = {
              isSignUpComplete: isComplete,
              nextStep: isComplete
                ? { signUpStep: 'DONE' }
                : {
                    signUpStep: 'CONFIRM_SIGN_UP',
                    codeDeliveryDetails: {
                      destination: res.code_delivery_details?.destination,
                      deliveryMedium: res.code_delivery_details
                        ?.delivery_medium as 'EMAIL' | 'SMS',
                      attributeName: res.code_delivery_details
                        ?.attribute_name as 'email' | 'phone_number',
                    },
                  },
              userId: undefined,
            };

            return output;
          }),
        ),
    );
  }

  confirmSignup(
    confirmationCode: string,
    username: string,
  ): Promise<ConfirmSignUpOutput> {
    return firstValueFrom(
      this.apiService
        .invoke(confirmSignup, {
          body: {
            email: username,
            code: confirmationCode,
          },
        })
        .pipe(
          map(() => {
            return {
              isSignUpComplete: true,
              nextStep: {
                signUpStep: 'DONE',
              },
            };
          }),
        ),
    );
  }

  resendSignupCode(username: string): Promise<ResendSignUpCodeOutput> {
    return firstValueFrom(
      this.apiService
        .invoke(resendCode, {
          body: {
            email: username,
          },
        })
        .pipe(
          map((res) => {
            if (!res.success) {
              throw new Error(res.message);
            }

            return {
              destination: res.code_delivery_details?.destination,
              deliveryMedium: res.code_delivery_details?.delivery_medium as
                | 'EMAIL'
                | 'SMS',
              attributeName: res.code_delivery_details?.attribute_name as
                | 'email'
                | 'phone_number',
            };
          }),
        ),
    );
  }

  resetPassword(username: string) {
    return this.auth.resetPassword({ username });
  }

  resetPasswordConfirm(username: string, code: string, newPassword: string) {
    return this.auth.confirmResetPassword({
      username,
      confirmationCode: code,
      newPassword,
    });
  }

  readonly jobCategories$ = this.apiService.invoke(getJobCategories).pipe(
    map(({ jobCategories }) => jobCategories),
    shareReplay(1),
  );
}
